// The shared standards behind the catalogue: the saved colours, the title blocks
// (cartigli), the bench and steam-bench presets and the pipe types — loaded from
// the database, and edited in their modal editors — plus Settings and Import
// heater from a datasheet. docs/claude/ui.md.
import {
  cloneSkirting,
  DEFAULT_BENCH_PRESETS,
  skirtingOf,
} from '../../shared/benchPresets';
import {
  defaultCartiglio,
  normalizeCartiglio,
} from '../../shared/cartiglio';
import {
  defaultName,
  dictDefaults,
  typedNames,
} from '../../shared/names';
import {
  DEFAULT_PIPE_TYPES,
  isBuiltinPipeType,
  mergePipeTypes,
  pipeOuter,
  pipeTypeDefaults,
  pipeTypeEntered,
  pipeTypeName,
  setPipeTypeName,
} from '../../shared/pipeTypes';
import {
  DEFAULT_SAUNA_PROFILE_PRESETS,
  type SaunaProfilePreset,
} from '../../shared/saunaProfilePresets';
import { DEFAULT_STEAM_BENCH_PRESETS } from '../../shared/steamBenchPresets';
import {
  type BenchSlatPreset,
  type Cartiglio,
  type CartiglioSource,
  type ConnectionType,
  type ImportedHeater,
  type PipeType,
  type ProductTemplate,
  type PropertyDef,
  type SavedColor,
  type SteamBenchPreset,
  type Vec2,
} from '../../shared/types';
import {
  normalizeProfile,
  profileArea,
  profileExtent,
  profileRing,
  profileSeatHeight,
} from '../benchProfile';
import {
  benchStructure,
  frontBoard,
  seatSlab,
} from '../benchSolids';
import {
  benchDepthFromSlats,
  benchSlatSpans,
  defaultOverlap,
} from '../benches';
import { refreshDrawingPipeType } from './connectionUi';
import {
  flashStatus,
  invalidate,
  newId,
  redrawBackdrop,
  renderProperties,
  state,
} from './core';
import {
  dxfClosedOutlines,
  type DxfOutline,
} from '../dxf';
import {
  HEATER_BODY_COLOR,
  HEATER_COVER_COLOR,
} from '../geometry';
import {
  DEFAULT_SLAT_THICKNESS,
  slatBase,
} from '../heights';
import {
  getUiLang,
  setButtonEnabled,
  tr,
  trIn,
  trMessage,
} from '../i18n';
import { STENAL_LOGO_DATA_URI } from '../logo';
import { nameDefaultsRows } from './nameFields';
import { platform } from '../platform';
import { titleEditor } from './titleEditor';
import {
  $,
  canEditStandards,
  escapeHtml,
  writeProblem,
  writeProduct,
} from '../main';
import { applyBenchSkirting } from '../roomBench';
import {
  buildSkirtingSection,
  buildSlatTable,
} from './slatTables';
import {
  infoRow,
  numberRow,
  parseMeasure,
  sectionTitle,
} from './panelRows';
import { openProductModal } from './productEditor';
import { editSteamProfile } from './profileEditor';

// Shared cartiglio (title-block) templates, loaded from Baserow at startup.
export let cartigli: Cartiglio[] = [defaultCartiglio()];
// Shared sauna-bench slat presets (doghe + gaps + overlap), loaded from Baserow.
export let benchPresets: BenchSlatPreset[] = [...DEFAULT_BENCH_PRESETS];
// Shared steam-bench profile presets (Tools ▸ Steam bench presets…), the built-in
// Steam A–D until the database answers.
export let steamPresets: SteamBenchPreset[] = DEFAULT_STEAM_BENCH_PRESETS.map((p) => ({ ...p, profile: p.profile.map((q) => ({ ...q })) }));
// Shared sauna profile presets (the sculpted bench's sauna list), the built-in
// Sauna A–C until the database answers.
export let saunaProfiles: SaunaProfilePreset[] = DEFAULT_SAUNA_PROFILE_PRESETS.map((p) => ({ ...p, profile: p.profile.map((q) => ({ ...q })) }));
// The catalogue's pipe types (Tools ▸ Pipe types…), the built-ins until the
// database answers.
export let pipeTypes: PipeType[] = mergePipeTypes([]);

/** Shared named-colour palette (Baserow), shown as quick-pick swatches in pickers. */
export let savedColors: SavedColor[] = [];
export async function loadSavedColors(): Promise<void> {
  try {
    savedColors = await platform.listColors();
  } catch {
    savedColors = [];
  }
}

/** Load the shared cartiglio (title-block) templates from Baserow. */
export async function loadCartigli(): Promise<void> {
  try {
    const list = await platform.listCartigli();
    // Never replace the cartigli array while the title block is being edited —
    // the periodic DB re-sync would otherwise clobber the in-progress edit (and,
    // mid-drag, drop the objects the pointer is moving). The edit is saved back
    // to the DB on exit, so the next refresh picks it up.
    if (list && list.length && !titleEditor.titleEdit) { cartigli = list; redrawBackdrop(); invalidate(); }
  } catch {
    /* keep the built-in default */
  }
}

/** Load the shared bench presets from Baserow: the sauna slat ones and the
 *  steam profiles (one table). */
export async function loadBenchPresets(): Promise<void> {
  try {
    const list = await platform.listBenchPresets();
    if (list && list.length) benchPresets = list;
  } catch {
    /* keep whatever we have */
  }
  try {
    const steam = await platform.listSteamBenchPresets();
    if (steam && steam.length) steamPresets = steam;
  } catch {
    /* keep whatever we have */
  }
  try {
    const sauna = await platform.listSaunaProfiles();
    if (sauna && sauna.length) saunaProfiles = sauna;
  } catch {
    /* keep whatever we have */
  }
}

/** Every standard from the database, side by side: each keeps what it has when
 *  its table does not answer. */
export async function loadStandards(): Promise<void> {
  await Promise.all([loadSavedColors(), loadCartigli(), loadBenchPresets(), loadPipeTypes()]);
}

/** A valid #rrggbb value for an <input type=color> (alpha stripped, else black). */
export function toHexInput(c: string): string {
  const m = /^#?([0-9a-f]{6})/i.exec(c.trim());
  return m ? `#${m[1].toLowerCase()}` : '#000000';
}

/** Open the colour editor, refreshing the palette from the database first. */
async function openColorEditor(): Promise<void> {
  await loadSavedColors(); // pull the latest palette
  renderColorEditorList();
  $('color-modal').classList.remove('hidden');
}

function closeColorEditor(): void {
  $('color-modal').classList.add('hidden');
}

// ---- Bench presets editor (Modify → Bench presets…) ----
// A 3-pane modal: preset list (left), the slat table (middle, same editor as the
// properties panel), and a top-view preview of the slats and gaps (right).
let editBench: BenchSlatPreset | null = null;

async function openBenchEditor(): Promise<void> {
  ($('bench-error') as HTMLElement).textContent = '';
  try { const list = await platform.listBenchPresets(); if (list.length) benchPresets = list; } catch { /* keep the cached list */ }
  const first = benchPresets.find((p) => p.isDefault) ?? benchPresets[0];
  selectBenchPreset(first?.id ?? '');
  $('bench-modal').classList.remove('hidden');
  requestAnimationFrame(() => drawBenchPreview()); // canvas is now laid out
}
function closeBenchEditor(): void { $('bench-modal').classList.add('hidden'); editBench = null; }

// Presets sorted alphabetically (numeric-aware, case-insensitive) for display —
// the underlying `benchPresets` order (and default/fallback logic) is untouched.
export function benchPresetsSorted(): BenchSlatPreset[] {
  return [...benchPresets].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
}

function renderBenchPresetList(): void {
  const list = $('bench-list'); list.innerHTML = '';
  for (const p of benchPresetsSorted()) {
    const el = document.createElement('div');
    el.className = 'cart-list-item' + (editBench && editBench.id === p.id ? ' active' : '');
    el.innerHTML = `<span>${escapeHtml(p.name)}</span>${p.isDefault ? `<span class="cart-badge">${escapeHtml(tr('default'))}</span>` : ''}`;
    el.addEventListener('click', () => selectBenchPreset(p.id));
    list.appendChild(el);
  }
}

function selectBenchPreset(id: string): void {
  const p = benchPresets.find((x) => x.id === id);
  editBench = p
    ? { rowId: p.rowId, id: p.id, name: p.name, isDefault: p.isDefault, slats: [...p.slats], gaps: [...(p.gaps ?? [])], slatThickness: p.slatThickness ? [...p.slatThickness] : undefined, overlap: p.overlap,
        ...(p.skirting ? { skirting: true } : {}), ...(p.skirtingBoards ? { skirtingBoards: cloneSkirting(p.skirtingBoards) } : {}) }
    : null;
  renderBenchPresetList();
  renderBenchEditorMain();
}

function renderBenchEditorMain(): void {
  const main = $('bench-slats'); main.innerHTML = '';
  const nameEl = $('bench-name') as HTMLInputElement;
  const defEl = $('bench-default') as HTMLInputElement;
  if (!editBench) { nameEl.value = ''; defEl.checked = false; drawBenchPreview(); return; }
  const eb = editBench;
  nameEl.value = eb.name;
  defEl.checked = !!eb.isDefault;
  buildSlatTable(main, eb, (patch) => {
    if ('slats' in patch) eb.slats = patch.slats ? [...patch.slats] : [];
    if ('gaps' in patch) eb.gaps = patch.gaps ? [...patch.gaps] : [];
    if ('slatThickness' in patch) eb.slatThickness = patch.slatThickness ? [...patch.slatThickness] : undefined;
    renderBenchEditorMain(); // rebuild the table + preview
  });
  main.appendChild(numberRow('Overlap (mm)', Math.round(eb.overlap ?? defaultOverlap({ depth: benchDepthFromSlats(eb) ?? 0, slats: eb.slats, gaps: eb.gaps })), (v) => { eb.overlap = Math.max(0, Math.round(v)); }, { commit: true }));
  main.appendChild(sectionTitle('Skirting'));
  buildSkirtingSection(main, eb, (patch) => { applyBenchSkirting(eb, patch); renderBenchEditorMain(); });
  drawBenchPreview();
}

/** Top-view preview of a preset: the slats as timber bars separated by their
 *  gaps, growing from the wall (top) into the room. */
function drawBenchPreview(): void {
  drawBenchSideView();
  const canvas = $<HTMLCanvasElement>('bench-preview-canvas');
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const W = Math.max(120, rect.width), H = Math.max(160, rect.height);
  canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const p = editBench;
  const slats = p?.slats ?? [], gaps = p?.gaps ?? [];
  if (!slats.length) {
    ctx.fillStyle = 'rgba(180,190,205,0.5)'; ctx.font = '12px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(tr('No slats'), W / 2, H / 2);
    return;
  }
  const total = slats.reduce((s, w) => s + Math.max(0, w), 0) + slats.reduce((s, _w, i) => s + Math.max(0, gaps[i] ?? 0), 0);
  const padX = 16, padTop = 22, padBot = 12;
  const availH = Math.max(1, H - padTop - padBot);
  const scale = total > 0 ? availH / total : 1;
  const barL = padX, barW = W - padX * 2;
  // Wall side label + line at the top.
  ctx.fillStyle = 'rgba(180,190,205,0.7)'; ctx.font = '10px sans-serif';
  ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
  ctx.fillText(tr('wall side ↓'), barL, padTop - 4);
  ctx.strokeStyle = 'rgba(150,160,175,0.6)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(barL, padTop - 1); ctx.lineTo(barL + barW, padTop - 1); ctx.stroke();
  let y = padTop;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let i = 0; i < slats.length; i++) {
    y += Math.max(0, gaps[i] ?? 0) * scale; // the gap before this slat (toward the wall)
    const h = Math.max(0, slats[i]) * scale;
    ctx.fillStyle = '#c68a4e';
    ctx.fillRect(barL, y, barW, h);
    ctx.strokeStyle = '#8a5a2a'; ctx.lineWidth = 1;
    ctx.strokeRect(barL + 0.5, y + 0.5, barW - 1, h - 1);
    if (h > 13) { ctx.fillStyle = '#3a2410'; ctx.font = '11px sans-serif'; ctx.fillText(`${Math.round(slats[i])}`, barL + barW / 2, y + h / 2); }
    y += h;
  }
}

/** The seat height the side view stands its bench at (mm). */
const SIDE_VIEW_SEAT = 450;

/** Side view of a preset: the bench cut across, wall on the left, standing on
 *  the floor at a {@link SIDE_VIEW_SEAT} seat — its trestle, frame and skirting
 *  under the slats (the last one the front board) — built by the very rules the
 *  section and 3D use (`benchStructure`, `skirtingLayout`, `slatBase`). */
function drawBenchSideView(): void {
  const canvas = $<HTMLCanvasElement>('bench-side-canvas');
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const W = Math.max(120, rect.width), H = Math.max(120, rect.height);
  canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const p = editBench;
  const spans = p ? benchSlatSpans(p) : [];
  if (!p || !spans.length) return;
  const last = spans[spans.length - 1];
  const D = last.off + last.width, top = SIDE_VIEW_SEAT, L = 1000, WALL = 40;
  const front = frontBoard(p);
  const F = front ? Math.min(D, front.d) : D;
  const solids = benchStructure({
    strip: (d0, d1) => [{ x: 0, y: d0 }, { x: L, y: d0 }, { x: L, y: d1 }, { x: 0, y: d1 }],
    at: (u, d) => ({ x: u * L, y: d }),
    across: (s, d) => ({ x: s, y: d }),
    trestles: [{ id: 'side', at: L / 2 }],
    len: L, depth: F, top, slab: seatSlab(p),
    frontBase: front ? top - front.hang : undefined,
    skirting: skirtingOf(p),
  });
  const pad = 14;
  const k = Math.min((W - pad * 2) / (D + WALL + 60), (H - pad * 2) / (top + 60));
  const x0 = pad + WALL * k, yFloor = H - pad;
  const box = (d0: number, d1: number, z0: number, z1: number, fill: string, stroke: string): void => {
    const x = x0 + d0 * k, y = yFloor - z1 * k, w = (d1 - d0) * k, h = (z1 - z0) * k;
    ctx.fillStyle = fill; ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = stroke; ctx.lineWidth = 1; ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, w - 1), Math.max(0, h - 1));
  };
  ctx.fillStyle = 'rgba(150,160,175,0.35)';
  ctx.fillRect(pad, pad, WALL * k, yFloor - pad);
  ctx.strokeStyle = 'rgba(150,160,175,0.6)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(pad, yFloor); ctx.lineTo(W - pad, yFloor); ctx.stroke();
  const ys = (poly: Vec2[]): [number, number] => [Math.min(...poly.map((q) => q.y)), Math.max(...poly.map((q) => q.y))];
  const tone: Record<string, [string, string]> = { support: ['#7a5a3a', '#4a3420'], frame: ['#8c6a46', '#4a3420'], skirting: ['#c68a4e', '#8a5a2a'] };
  for (const part of ['support', 'frame', 'skirting'] as const) {
    for (const sd of solids.filter((q) => q.part === part)) {
      const [d0, d1] = ys(sd.poly);
      if (!sd.sweep) { box(d0, d1, sd.base, sd.top, ...tone[part]); continue; }
      // A skirting batten: its profile, the slanted top under the frame.
      ctx.beginPath();
      sd.sweep.profile.forEach((q, i) => (i ? ctx.lineTo : ctx.moveTo).call(ctx, x0 + (d0 + q.x) * k, yFloor - q.y * k));
      ctx.closePath();
      ctx.fillStyle = tone[part][0]; ctx.fill();
      ctx.strokeStyle = tone[part][1]; ctx.lineWidth = 1; ctx.stroke();
    }
  }
  spans.forEach((sp, i) => box(sp.off, sp.off + sp.width, slatBase(p, i, top), top, '#c68a4e', '#8a5a2a'));
}

async function benchEditorSave(): Promise<void> {
  if (!canEditStandards() || !editBench) return;
  const err = $('bench-error') as HTMLElement;
  editBench.name = ($('bench-name') as HTMLInputElement).value.trim();
  editBench.isDefault = ($('bench-default') as HTMLInputElement).checked;
  if (!editBench.name) { err.textContent = tr('Enter a preset name first.'); return; }
  if (!editBench.slats.length) { err.textContent = tr('Add at least one slat.'); return; }
  try {
    const saved = await platform.saveBenchPreset(editBench);
    if (saved.isDefault) for (const p of benchPresets) if (p.id !== saved.id) p.isDefault = false;
    const i = benchPresets.findIndex((p) => p.id === saved.id);
    if (i >= 0) benchPresets[i] = saved; else benchPresets.push(saved);
    selectBenchPreset(saved.id);
    err.textContent = tr('Saved.');
  } catch {
    err.textContent = tr('Could not save the preset to the database.');
  }
}

async function benchEditorDelete(): Promise<void> {
  if (!canEditStandards() || !editBench) return;
  const target = editBench;
  if (target.rowId != null) {
    try { await platform.deleteBenchPreset(target.rowId); } catch { ($('bench-error') as HTMLElement).textContent = tr('Could not delete from the database.'); return; }
  }
  benchPresets = benchPresets.filter((p) => p.id !== target.id);
  selectBenchPreset(benchPresets[0]?.id ?? '');
}

function benchEditorNew(): void {
  // Draft a new preset by copying the currently-selected one (its slats/gaps/
  // thickness/overlap), so the user tweaks from a known composition rather than a
  // blank default. Falls back to a plain default when nothing is selected. The
  // copy gets a fresh id and no rowId (unsaved) and is never the default.
  const src = editBench;
  const preset: BenchSlatPreset = src
    ? {
        id: 'preset-' + Date.now(),
        name: src.name?.trim() ? tr('{name} copy').replace('{name}', src.name) : tr('New preset'),
        slats: [...src.slats],
        gaps: [...(src.gaps ?? [])],
        slatThickness: src.slatThickness ? [...src.slatThickness] : undefined,
        overlap: src.overlap,
        ...(src.skirting ? { skirting: true } : {}),
        ...(src.skirtingBoards ? { skirtingBoards: cloneSkirting(src.skirtingBoards) } : {}),
      }
    : {
        id: 'preset-' + Date.now(), name: tr('New preset'),
        slats: [90, 90, 90], gaps: [10, 10, 10], slatThickness: [DEFAULT_SLAT_THICKNESS, DEFAULT_SLAT_THICKNESS, DEFAULT_SLAT_THICKNESS],
      };
  benchPresets.push(preset);
  selectBenchPreset(preset.id);
  const nameEl = $('bench-name') as HTMLInputElement;
  nameEl.focus(); nameEl.select();
}

// ---- Bench profiles (Tools ▸ Steam bench presets… / Sauna profiles…) ----
// The same 3-pane layout as the slat presets: the list, the preset (name, its
// profile imported from a DXF, its figures) and the profile drawn in section.
// One window for both lists: a steam room's, and a sauna's for its sculpted
// benches (the List pick switches between them).
let editSteam: SteamBenchPreset | null = null;
let profileKind: 'steam' | 'sauna' = 'steam';
const kindList = (): SteamBenchPreset[] => (profileKind === 'sauna' ? saunaProfiles : steamPresets);
function setKindList(list: SteamBenchPreset[]): void { if (profileKind === 'sauna') saunaProfiles = list; else steamPresets = list; }
const kindSorted = (): SteamBenchPreset[] => [...kindList()].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
// The closed outlines of the DXF last opened in the window, for the Outline pick.
let steamOutlines: DxfOutline[] = [];

async function openSteamEditor(kind: 'steam' | 'sauna' = 'steam'): Promise<void> {
  ($('steam-modal')).classList.remove('hidden');
  await showProfileList(kind);
}

/** The window on one list: its rows fetched again, its first selected. */
async function showProfileList(kind: 'steam' | 'sauna'): Promise<void> {
  profileKind = kind;
  ($('steam-kind') as HTMLSelectElement).value = kind;
  ($('steam-error') as HTMLElement).textContent = '';
  steamOutlines = [];
  $('steam-pick-row').classList.add('hidden');
  try {
    const list = kind === 'sauna' ? await platform.listSaunaProfiles() : await platform.listSteamBenchPresets();
    if (list.length && profileKind === kind) setKindList(list);
  } catch { /* keep the cached list */ }
  selectSteamPreset(kindSorted()[0]?.id ?? '');
  requestAnimationFrame(() => drawSteamPreview());
}
function closeSteamEditor(): void { $('steam-modal').classList.add('hidden'); editSteam = null; }

function renderSteamPresetList(): void {
  const list = $('steam-list'); list.innerHTML = '';
  for (const p of kindSorted()) {
    const el = document.createElement('div');
    const active = !!editSteam && editSteam.id === p.id;
    el.className = 'cart-list-item' + (active ? ' active' : '');
    el.innerHTML = `<span>${escapeHtml(active ? editSteam!.name : p.name)}</span>`;
    el.addEventListener('click', () => selectSteamPreset(p.id));
    list.appendChild(el);
  }
}

function selectSteamPreset(id: string): void {
  const p = kindList().find((x) => x.id === id);
  editSteam = p ? { ...p, profile: p.profile.map((q) => ({ ...q })) } : null;
  renderSteamPresetList();
  renderSteamEditorMain();
}

function renderSteamEditorMain(): void {
  ($('steam-name') as HTMLInputElement).value = editSteam?.name ?? '';
  const fig = $('steam-figures'); fig.innerHTML = '';
  if (editSteam && editSteam.profile.length >= 3) {
    const ext = profileExtent(editSteam.profile);
    fig.appendChild(infoRow('Depth (mm)', String(Math.round(ext.width))));
    fig.appendChild(infoRow('Seat height (mm)', String(Math.round(profileSeatHeight(editSteam.profile)))));
    fig.appendChild(infoRow('Overall height (mm)', String(Math.round(ext.height))));
  }
  drawSteamPreview();
}

/** The profile in section: the wall on the left, the floor below, to scale. */
function drawSteamPreview(): void {
  const canvas = $<HTMLCanvasElement>('steam-preview-canvas');
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const W = Math.max(120, rect.width), H = Math.max(160, rect.height);
  canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const ring = editSteam && editSteam.profile.length >= 3 ? profileRing(editSteam.profile) : [];
  if (!ring.length) {
    ctx.fillStyle = 'rgba(180,190,205,0.5)'; ctx.font = '12px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(tr('No profile'), W / 2, H / 2);
    return;
  }
  const { width, height } = profileExtent(editSteam!.profile);
  const pad = 18;
  const k = Math.min((W - 2 * pad) / Math.max(1, width), (H - 2 * pad) / Math.max(1, height));
  const x0 = pad, y0 = H - pad;
  ctx.strokeStyle = 'rgba(150,160,175,0.7)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(x0 - 1, pad - 6); ctx.lineTo(x0 - 1, y0 + 1); ctx.lineTo(W - pad + 6, y0 + 1); ctx.stroke();
  ctx.beginPath();
  ring.forEach((p, i) => { const X = x0 + p.x * k, Y = y0 - p.y * k; if (i) ctx.lineTo(X, Y); else ctx.moveTo(X, Y); });
  ctx.closePath();
  ctx.fillStyle = '#d9d4cb'; ctx.fill();
  ctx.strokeStyle = '#5b5348'; ctx.lineWidth = 1.2; ctx.stroke();
}

/** A DXF picked in the window: its closed outlines; the first (largest, or the
 *  one labelled like the preset's name) becomes the profile, the others are
 *  offered in the Outline pick. */
async function steamImportFile(file: File): Promise<void> {
  const err = $('steam-error') as HTMLElement;
  err.textContent = '';
  let outlines: DxfOutline[] = [];
  try { outlines = dxfClosedOutlines(await file.text()); } catch { /* reported below */ }
  if (!outlines.length) { err.textContent = tr('No closed outline found in that DXF.'); return; }
  steamOutlines = outlines;
  const pick = $('steam-pick') as HTMLSelectElement;
  pick.innerHTML = '';
  outlines.forEach((o, i) => {
    const ext = profileExtent(normalizeProfile(o.points));
    const opt = document.createElement('option');
    opt.value = String(i);
    opt.textContent = `${o.label ? `${o.label} — ` : ''}${Math.round(ext.width)} × ${Math.round(ext.height)} mm`;
    pick.appendChild(opt);
  });
  $('steam-pick-row').classList.toggle('hidden', outlines.length < 2);
  const name = editSteam?.name.trim().toLowerCase() ?? '';
  const named = outlines.findIndex((o) => o.label && name.endsWith(' ' + o.label.toLowerCase()));
  pick.value = String(Math.max(0, named));
  steamUseOutline(Math.max(0, named));
}

function steamUseOutline(i: number): void {
  const o = steamOutlines[i];
  if (!o) return;
  if (!editSteam) steamEditorNew();
  editSteam!.profile = normalizeProfile(o.points);
  renderSteamEditorMain();
}

async function steamEditorSave(): Promise<void> {
  if (!canEditStandards() || !editSteam) return;
  const err = $('steam-error') as HTMLElement;
  editSteam.name = ($('steam-name') as HTMLInputElement).value.trim();
  if (!editSteam.name) { err.textContent = tr('Enter a preset name first.'); return; }
  if (editSteam.profile.length < 3 || profileArea(editSteam.profile) < 1) { err.textContent = tr('Import a profile first.'); return; }
  try {
    const saved = profileKind === 'sauna' ? await platform.saveSaunaProfile(editSteam) : await platform.saveSteamBenchPreset(editSteam);
    const list = kindList();
    const i = list.findIndex((p) => p.id === saved.id);
    if (i >= 0) list[i] = saved; else list.push(saved);
    selectSteamPreset(saved.id);
    err.textContent = tr('Saved.');
  } catch {
    err.textContent = tr('Could not save the preset to the database.');
  }
}

async function steamEditorDelete(): Promise<void> {
  if (!canEditStandards() || !editSteam) return;
  const target = editSteam;
  if (target.rowId != null) {
    try { await platform.deleteBenchPreset(target.rowId); } catch { ($('steam-error') as HTMLElement).textContent = tr('Could not delete from the database.'); return; }
  }
  setKindList(kindList().filter((p) => p.id !== target.id));
  selectSteamPreset(kindSorted()[0]?.id ?? '');
}

/** The preset's profile on the drawing (ui/profileEditor.ts): `fresh`, a new
 *  preset drawn from nothing. The window comes back with it, unsaved, on Done
 *  or Cancel. */
function steamEditProfile(fresh: boolean): void {
  if (fresh) {
    const preset: SteamBenchPreset = { id: `${profileKind}-${Date.now()}`, name: tr('New preset'), profile: [] };
    kindList().push(preset);
    selectSteamPreset(preset.id);
  }
  if (!editSteam) return;
  const target = editSteam;
  target.name = ($('steam-name') as HTMLInputElement).value.trim() || target.name;
  $('steam-modal').classList.add('hidden');
  editSteamProfile(target.name, target.profile, (profile) => {
    target.profile = profile;
    ($('steam-error') as HTMLElement).textContent = tr('Profile drawn: Save to keep it.');
  }, () => {
    $('steam-modal').classList.remove('hidden');
    renderSteamPresetList();
    renderSteamEditorMain();
    requestAnimationFrame(() => drawSteamPreview());
  });
}

/** A new preset, drafted from the one selected (its profile), unsaved. */
function steamEditorNew(): void {
  const src = editSteam;
  const preset: SteamBenchPreset = {
    id: `${profileKind}-${Date.now()}`,
    name: src?.name.trim() ? `${src.name} ${tr('copy')}` : tr('New preset'),
    profile: src ? src.profile.map((q) => ({ ...q })) : [],
  };
  kindList().push(preset);
  selectSteamPreset(preset.id);
  const nameEl = $('steam-name') as HTMLInputElement;
  nameEl.focus(); nameEl.select();
}

// ---- Pipe types (Tools ▸ Pipe types…) ----
// One list and a form: a built-in can be edited (saved as a row of its id) but
// not deleted — deleting its row brings the built-in back.
let editPipe: PipeType | null = null;

/** The catalogue's pipe types: the built-ins merged with the Piping table. */
export async function loadPipeTypes(): Promise<void> {
  try { const list = await platform.listPipeTypes(); if (list?.length) pipeTypes = list; } catch { /* keep the built-ins */ }
}

function pipeTypesSorted(): PipeType[] {
  return [...pipeTypes].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
}

async function openPipeEditor(): Promise<void> {
  ($('pipe-error') as HTMLElement).textContent = '';
  await loadPipeTypes();
  selectPipeType(pipeTypesSorted()[0]?.id ?? '');
  $('pipe-modal').classList.remove('hidden');
}
function closePipeEditor(): void { $('pipe-modal').classList.add('hidden'); editPipe = null; }

function renderPipeTypeList(): void {
  const list = $('pipe-list'); list.innerHTML = '';
  for (const t of pipeTypesSorted()) {
    const el = document.createElement('div');
    const active = !!editPipe && editPipe.id === t.id;
    el.className = 'cart-list-item' + (active ? ' active' : '');
    el.innerHTML = `<span>${escapeHtml(pipeTypeName(active ? editPipe! : t, getUiLang()))}</span>`;
    el.addEventListener('click', () => selectPipeType(t.id));
    list.appendChild(el);
  }
}

function selectPipeType(id: string): void {
  const t = pipeTypes.find((x) => x.id === id);
  editPipe = t ? { ...t, nameI18n: t.nameI18n && { ...t.nameI18n } } : null;
  ($('pipe-error') as HTMLElement).textContent = '';
  renderPipeTypeList();
  renderPipeEditorMain();
}

function renderPipeEditorMain(): void {
  const t = editPipe;
  const val = (id: string, v: string): void => { ($(id) as HTMLInputElement).value = v; };
  val('pipe-name', t ? pipeTypeEntered(t) : '');
  const defaults = t && pipeTypeDefaults(t.id);
  ($('pipe-name') as HTMLInputElement).placeholder = defaults ? defaultName(defaults, getUiLang()) : '';
  $('pipe-name-defaults').replaceChildren(...(defaults ? [nameDefaultsRows(defaults)] : []));
  $('pipe-name-defaults-row').classList.toggle('hidden', !defaults);
  val('pipe-material', t?.material ?? '');
  ($('pipe-discipline') as HTMLSelectElement).value = t?.discipline ?? 'plumbing';
  val('pipe-inner', t ? String(t.inner) : '');
  val('pipe-wall', t ? String(t.wall) : '');
  val('pipe-bend', t ? String(t.bendRadius) : '');
  val('pipe-slope', t ? String(t.slope) : '');
  ($('pipe-flexible') as HTMLInputElement).checked = !!t?.flexible;
  const fig = $('pipe-figures'); fig.innerHTML = '';
  if (t) fig.appendChild(infoRow('Outer diameter (mm)', String(Math.round(pipeOuter(t) * 10) / 10)));
  const del = $('pipe-delete') as HTMLButtonElement;
  const builtin = !!t && isBuiltinPipeType(t.id);
  setButtonEnabled(del, !!t && (!builtin || t.rowId != null), 'A built-in type can be edited, not deleted');
  del.textContent = builtin ? tr('Restore built-in') : tr('Delete');
}

/** The form read back into `editPipe`; an error message when a figure is wrong. */
function readPipeForm(): string | null {
  const t = editPipe;
  if (!t) return null;
  const name = ($('pipe-name') as HTMLInputElement).value.trim();
  if (!name && !pipeTypeDefaults(t.id)) return tr('Enter a name first.');
  const mm = (id: string): number | null => parseMeasure(($(id) as HTMLInputElement).value);
  const inner = mm('pipe-inner'), wall = mm('pipe-wall'), bend = mm('pipe-bend');
  const slope = Number(($('pipe-slope') as HTMLInputElement).value.trim().replace(',', '.').replace('%', ''));
  if (inner == null || inner <= 0) return tr('The internal diameter must be more than 0.');
  if (wall == null || wall < 0 || bend == null || bend < 0 || !Number.isFinite(slope) || slope < 0) return tr('Check the figures: none can be negative.');
  setPipeTypeName(t, name);
  Object.assign(t, {
    material: ($('pipe-material') as HTMLInputElement).value.trim(),
    discipline: ($('pipe-discipline') as HTMLSelectElement).value as ConnectionType,
    inner, wall, bendRadius: bend, slope,
  });
  if (($('pipe-flexible') as HTMLInputElement).checked) t.flexible = true;
  else delete t.flexible;
  return null;
}

async function pipeEditorSave(): Promise<void> {
  if (!canEditStandards() || !editPipe) return;
  const err = $('pipe-error') as HTMLElement;
  const bad = readPipeForm();
  if (bad) { err.textContent = bad; return; }
  try {
    const saved = await platform.savePipeType(editPipe);
    const i = pipeTypes.findIndex((t) => t.id === saved.id);
    if (i >= 0) pipeTypes[i] = saved; else pipeTypes.push(saved);
    refreshDrawingPipeType(saved);
    selectPipeType(saved.id);
    err.textContent = tr('Saved.');
  } catch {
    err.textContent = tr('Could not save the type to the database.');
  }
}

async function pipeEditorDelete(): Promise<void> {
  if (!canEditStandards() || !editPipe) return;
  const target = editPipe;
  if (target.rowId != null) {
    try { await platform.deletePipeType(target.rowId); } catch { ($('pipe-error') as HTMLElement).textContent = tr('Could not delete from the database.'); return; }
  }
  const builtin = DEFAULT_PIPE_TYPES.find((t) => t.id === target.id);
  pipeTypes = builtin
    ? pipeTypes.map((t) => (t.id === target.id ? { ...builtin, nameI18n: builtin.nameI18n && { ...builtin.nameI18n } } : t))
    : pipeTypes.filter((t) => t.id !== target.id);
  if (builtin) refreshDrawingPipeType(builtin);
  selectPipeType(builtin ? builtin.id : pipeTypesSorted()[0]?.id ?? '');
}

/** A new type, drafted from the one selected, unsaved. */
function pipeEditorNew(): void {
  const src = editPipe ?? DEFAULT_PIPE_TYPES[0];
  const t: PipeType = {
    ...src, rowId: undefined, nameI18n: undefined,
    id: 'pipe-' + Date.now(),
    name: `${pipeTypeName(src, getUiLang())} ${tr('copy')}`,
  };
  delete t.rowId; delete t.nameI18n;
  pipeTypes.push(t);
  selectPipeType(t.id);
  const nameEl = $('pipe-name') as HTMLInputElement;
  nameEl.focus(); nameEl.select();
}

// ---- Settings (Modify → Settings…): BYO Anthropic key ----

async function openSettings(): Promise<void> {
  ($('set-error') as HTMLElement).textContent = '';
  try {
    const s = await platform.getSettings();
    ($('set-anthropic-key') as HTMLInputElement).value = s.anthropicApiKey ?? '';
  } catch {
    ($('set-anthropic-key') as HTMLInputElement).value = '';
  }
  ($('set-keep-tool') as HTMLInputElement).checked = state.keepToolActive;
  $('settings-modal').classList.remove('hidden');
  ($('set-anthropic-key') as HTMLInputElement).focus();
}

function closeSettings(): void {
  $('settings-modal').classList.add('hidden');
}

async function saveSettingsFromModal(): Promise<void> {
  const key = ($('set-anthropic-key') as HTMLInputElement).value.trim();
  const keepToolActive = ($('set-keep-tool') as HTMLInputElement).checked;
  try {
    await platform.saveSettings({ anthropicApiKey: key, keepToolActive });
    state.keepToolActive = keepToolActive;
    closeSettings();
  } catch (err) {
    ($('set-error') as HTMLElement).textContent = tr('Could not save settings') + ': ' + (err as Error).message;
  }
}

// ---- Cartigli editor (Modify → Cartigli…) ----

let editCart: Cartiglio | null = null; // working copy of the selected cartiglio

export const CART_SOURCES: Array<[CartiglioSource, string]> = [
  ['descrizione', 'Project no.'], ['commessa', 'Customer'],
  ['versione', 'Version'],
  ['data', 'Date'], ['autore', 'Author'], ['foglio', 'Sheet (format)'],
  ['scala', 'Scale'], ['blank', 'Blank (manual)'], ['literal', 'Fixed text'],
];

async function openCartigliEditor(): Promise<void> {
  ($('cart-error') as HTMLElement).textContent = '';
  await loadCartigli();
  renderCartList();
  selectCart((cartigli.find((c) => c.isDefault) || cartigli[0])?.id || '');
  $('cart-modal').classList.remove('hidden');
}
function closeCartigliEditor(): void { $('cart-modal').classList.add('hidden'); editCart = null; }

function renderCartList(): void {
  const list = $('cart-list');
  list.innerHTML = '';
  for (const c of cartigli) {
    const el = document.createElement('div');
    el.className = 'cart-list-item' + (editCart && editCart.id === c.id ? ' active' : '');
    el.innerHTML = `<span>${escapeHtml(c.name)}</span>${c.isDefault ? `<span class="cart-badge">${escapeHtml(tr('default'))}</span>` : ''}`;
    el.addEventListener('click', () => selectCart(c.id));
    list.appendChild(el);
  }
}

function selectCart(id: string): void {
  const src = cartigli.find((c) => c.id === id) || defaultCartiglio();
  editCart = normalizeCartiglio(JSON.parse(JSON.stringify(src)) as Cartiglio, src.rowId);
  renderCartList();
  renderCartForm();
}

function renderCartForm(): void {
  if (!editCart) return;
  const c = editCart;
  const val = (id: string, v: string): void => { ($(id) as HTMLInputElement).value = v; };
  val('cart-name', c.name);
  ($('cart-default') as HTMLInputElement).checked = c.isDefault;
  val('cart-company', c.company);
  val('cart-address', c.addressLines.join('\n'));
  val('cart-copyright', c.legal.copyright);
  val('cart-disclaimer', c.legal.disclaimer);
  val('cart-signature', c.legal.signature);
  const prev = $('cart-logo-prev') as HTMLImageElement;
  prev.src = c.logoDataUri || STENAL_LOGO_DATA_URI;
}

function readCartFormInto(c: Cartiglio): void {
  c.name = ($('cart-name') as HTMLInputElement).value.trim() || 'Cartiglio';
  c.isDefault = ($('cart-default') as HTMLInputElement).checked;
  c.company = ($('cart-company') as HTMLInputElement).value.trim();
  c.addressLines = ($('cart-address') as HTMLTextAreaElement).value.split('\n').map((l) => l.trim()).filter(Boolean);
  c.legal.copyright = ($('cart-copyright') as HTMLTextAreaElement).value.trim();
  c.legal.disclaimer = ($('cart-disclaimer') as HTMLTextAreaElement).value.trim();
  c.legal.signature = ($('cart-signature') as HTMLInputElement).value.trim();
}

async function saveCartiglioFromModal(): Promise<void> {
  if (!canEditStandards() || !editCart) return;
  readCartFormInto(editCart);
  ($('cart-error') as HTMLElement).textContent = '';
  try {
    const saved = await platform.saveCartiglio(editCart);
    // Enforce a single default locally, then refresh from the source.
    if (saved.isDefault) cartigli.forEach((c) => { if (c.id !== saved.id) c.isDefault = false; });
    const i = cartigli.findIndex((c) => c.id === saved.id);
    if (i >= 0) cartigli[i] = saved; else cartigli.push(saved);
    editCart = saved;
    redrawBackdrop(); invalidate();
    renderCartList();
    flashStatus(tr('Title block saved.'));
  } catch (err) {
    ($('cart-error') as HTMLElement).textContent = tr('Save failed: ') + (err as Error).message;
  }
}

function newCartiglio(): void {
  const c = defaultCartiglio();
  c.id = `cart-${Date.now()}`;
  c.name = tr('New title block');
  c.isDefault = false;
  c.rowId = undefined;
  cartigli.push(c);
  renderCartList();
  selectCart(c.id);
}

async function deleteCartiglioFromModal(): Promise<void> {
  if (!canEditStandards() || !editCart) return;
  if (cartigli.length <= 1) { ($('cart-error') as HTMLElement).textContent = tr('At least one title block must remain.'); return; }
  const target = editCart;
  try {
    if (target.rowId != null) await platform.deleteCartiglio(target.rowId);
    cartigli = cartigli.filter((c) => c.id !== target.id);
    if (!cartigli.some((c) => c.isDefault)) cartigli[0].isDefault = true;
    redrawBackdrop(); invalidate();
    renderCartList();
    selectCart(cartigli[0].id);
  } catch (err) {
    ($('cart-error') as HTMLElement).textContent = tr('Delete failed: ') + (err as Error).message;
  }
}

function onCartLogoFile(e: Event): void {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file || !editCart) return;
  const reader = new FileReader();
  reader.onload = () => { if (editCart) { editCart.logoDataUri = String(reader.result); ($('cart-logo-prev') as HTMLImageElement).src = editCart.logoDataUri; invalidate(); } };
  reader.readAsDataURL(file);
}

// ---- Import heater from datasheet (Modify → Import Heater…) ----

/** Build a heater ProductTemplate from the specs extracted from a datasheet. */
function heaterTemplateFromImport(h: ImportedHeater): ProductTemplate {
  const typed = [h.brand, h.product].filter(Boolean).join(' ').trim();
  // Its brand and model read the same in every language.
  const names = typed ? typedNames(typed) : { name: 'Imported heater', nameI18n: dictDefaults('Imported heater', trIn) };
  const props: PropertyDef[] = [];
  if (h.brand) props.push({ key: 'brand', label: 'Brand', value: h.brand });
  if (h.product) props.push({ key: 'model', label: 'Model', value: h.product });
  return {
    id: newId('prod'),
    ...names,
    category: 'heater',
    width: h.widthMm,
    depth: h.depthMm,
    color: HEATER_BODY_COLOR,
    heaterHeight: h.heightMm || undefined,
    heightFromGround: h.heightFromGroundMm || undefined,
    heaterGap: { front: h.gapFrontMm, back: h.gapBackMm, left: h.gapLeftMm, right: h.gapRightMm },
    heaterGuardColor: HEATER_COVER_COLOR,
    properties: props,
    builtin: false,
  };
}

function openImportHeater(): void {
  ($('ih-url') as HTMLInputElement).value = '';
  ($('ih-status') as HTMLElement).textContent = '';
  ($('ih-error') as HTMLElement).textContent = '';
  ($('ih-import') as HTMLButtonElement).disabled = false;
  $('importheater-modal').classList.remove('hidden');
  ($('ih-url') as HTMLInputElement).focus();
}

function closeImportHeater(): void {
  $('importheater-modal').classList.add('hidden');
}

async function runImportHeater(): Promise<void> {
  const url = ($('ih-url') as HTMLInputElement).value.trim();
  const status = $('ih-status') as HTMLElement;
  const error = $('ih-error') as HTMLElement;
  const btn = $('ih-import') as HTMLButtonElement;
  error.textContent = '';
  if (!url) { error.textContent = tr('Paste a link to a datasheet PDF.'); return; }
  btn.disabled = true;
  status.textContent = tr('Reading the datasheet with AI… this can take up to a minute.');
  try {
    const res = await platform.importHeater(url);
    if (!res.ok || !res.heater) {
      error.textContent = res.error ? trMessage(res.error) : tr('Could not read that datasheet.');
      status.textContent = '';
      btn.disabled = false;
      return;
    }
    // Add the imported heater to the catalogue, then open it for review.
    const template = heaterTemplateFromImport(res.heater);
    const saved = await writeProduct(template);
    if (!saved.ok) {
      error.textContent = writeProblem(saved);
      status.textContent = '';
      btn.disabled = false;
      return;
    }
    closeImportHeater();
    openProductModal(state.userProducts.find((p) => p.id === template.id) ?? template); // let the user review / tweak the extracted values
  } catch (err) {
    error.textContent = tr('Import failed') + ': ' + (err as Error).message;
    status.textContent = '';
    btn.disabled = false;
  }
}

/** Re-render any open colour pickers so edited swatches update live. */
function refreshColorPickers(): void {
  renderProperties();
}

/** Persist one colour (create or update) and record its assigned row id. */
async function persistColor(sc: SavedColor): Promise<void> {
  if (!canEditStandards()) return;
  try {
    const saved = await platform.saveColor(sc);
    sc.id = saved.id;
  } catch (err) {
    console.error('Failed to save colour', err);
  }
  refreshColorPickers();
}

function renderColorEditorList(): void {
  const list = $('color-list');
  list.innerHTML = '';
  if (!savedColors.length) {
    const e = document.createElement('div');
    e.className = 'hint';
    e.textContent = tr('No saved colours yet — add one below.');
    list.appendChild(e);
    return;
  }
  // Read-only users (no `edit` claim) can view the palette but not change it.
  const roColors = !canEditStandards();
  savedColors.forEach((sc, i) => {
    const row = document.createElement('div');
    row.className = 'color-row';
    const swatch = document.createElement('input');
    swatch.type = 'color';
    swatch.value = toHexInput(sc.color);
    swatch.title = tr('Edit colour');
    swatch.disabled = roColors;
    swatch.addEventListener('change', () => { sc.color = swatch.value; void persistColor(sc); });
    const name = document.createElement('input');
    name.type = 'text';
    name.value = sc.name;
    name.placeholder = tr('Colour name');
    name.disabled = roColors;
    name.addEventListener('change', () => { sc.name = name.value.trim(); void persistColor(sc); });
    const del = document.createElement('button');
    del.className = 'del mini';
    del.textContent = '✕';
    del.title = tr('Delete colour');
    if (roColors) del.style.display = 'none';
    del.addEventListener('click', async () => {
      if (sc.id != null) { try { await platform.deleteColor(sc.id); } catch (err) { console.error('Failed to delete colour', err); } }
      savedColors.splice(i, 1);
      renderColorEditorList();
      refreshColorPickers();
    });
    row.append(swatch, name, del);
    list.appendChild(row);
  });
}

/** Add the colour typed into the editor's "new colour" fields to the palette. */
async function addColorFromEditor(): Promise<void> {
  if (!canEditStandards()) return;
  const nameEl = $<HTMLInputElement>('ce-new-name');
  const colorEl = $<HTMLInputElement>('ce-new-color');
  const nc: SavedColor = { name: nameEl.value.trim(), color: colorEl.value };
  try {
    savedColors.push(await platform.saveColor(nc));
  } catch (err) {
    console.error('Failed to add colour', err);
    savedColors.push(nc);
  }
  nameEl.value = '';
  renderColorEditorList();
  refreshColorPickers();
}

/** The standards' Tools-menu entries and the controls of their modal editors. */
export function wireStandards(): void {
  platform.onMenu('menu:colorEditor', () => { void openColorEditor(); });
  platform.onMenu('menu:cartigli', () => { void openCartigliEditor(); });
  platform.onMenu('menu:benchPresets', () => { void openBenchEditor(); });
  platform.onMenu('menu:steamBenchPresets', () => { void openSteamEditor('steam'); });
  platform.onMenu('menu:saunaProfiles', () => { void openSteamEditor('sauna'); });
  platform.onMenu('menu:pipeTypes', () => { void openPipeEditor(); });
  platform.onMenu('menu:importHeater', openImportHeater);
  platform.onMenu('menu:settings', () => { void openSettings(); });
  $('ce-close').addEventListener('click', closeColorEditor);
  $('ce-add').addEventListener('click', () => { void addColorFromEditor(); });
  $('set-cancel').addEventListener('click', closeSettings);
  $('set-save').addEventListener('click', () => { void saveSettingsFromModal(); });
  $('cart-new').addEventListener('click', newCartiglio);
  $('cart-close').addEventListener('click', closeCartigliEditor);
  $('cart-save').addEventListener('click', () => { void saveCartiglioFromModal(); });
  $('cart-delete').addEventListener('click', () => { void deleteCartiglioFromModal(); });
  $('cart-logo-file').addEventListener('change', onCartLogoFile);
  $('cart-logo-clear').addEventListener('click', () => { if (editCart) { editCart.logoDataUri = undefined; ($('cart-logo-prev') as HTMLImageElement).src = STENAL_LOGO_DATA_URI; invalidate(); } });
  $('bench-new').addEventListener('click', benchEditorNew);
  $('bench-close').addEventListener('click', closeBenchEditor);
  $('bench-save').addEventListener('click', () => { void benchEditorSave(); });
  $('bench-delete').addEventListener('click', () => { void benchEditorDelete(); });
  $('bench-name').addEventListener('input', () => { if (editBench) { editBench.name = ($('bench-name') as HTMLInputElement).value; renderBenchPresetList(); } });
  $('pipe-new').addEventListener('click', pipeEditorNew);
  $('pipe-close').addEventListener('click', closePipeEditor);
  $('pipe-save').addEventListener('click', () => { void pipeEditorSave(); });
  $('pipe-delete').addEventListener('click', () => { void pipeEditorDelete(); });
  $('steam-new').addEventListener('click', steamEditorNew);
  $('steam-edit-profile').addEventListener('click', () => steamEditProfile(false));
  $('steam-draw-profile').addEventListener('click', () => steamEditProfile(true));
  $('steam-kind').addEventListener('change', () => { void showProfileList(($('steam-kind') as HTMLSelectElement).value === 'sauna' ? 'sauna' : 'steam'); });
  $('steam-close').addEventListener('click', closeSteamEditor);
  $('steam-save').addEventListener('click', () => { void steamEditorSave(); });
  $('steam-delete').addEventListener('click', () => { void steamEditorDelete(); });
  $('steam-import').addEventListener('click', () => ($('steam-file') as HTMLInputElement).click());
  $('steam-file').addEventListener('change', () => {
    const input = $('steam-file') as HTMLInputElement;
    const f = input.files?.[0];
    input.value = '';
    if (f) void steamImportFile(f);
  });
  $('steam-pick').addEventListener('change', () => steamUseOutline(Number(($('steam-pick') as HTMLSelectElement).value)));
  $('steam-name').addEventListener('input', () => { if (editSteam) { editSteam.name = ($('steam-name') as HTMLInputElement).value; renderSteamPresetList(); } });
  $('bench-default').addEventListener('change', () => { if (editBench) editBench.isDefault = ($('bench-default') as HTMLInputElement).checked; });
  $('ih-cancel').addEventListener('click', closeImportHeater);
  $('ih-import').addEventListener('click', () => { void runImportHeater(); });
  $('ih-url').addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') void runImportHeater(); });
}
